import { Link, router } from '@inertiajs/react'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { MAP_CONTAINER_ID, MAP_ROOT_PATH } from '../../lib/map_config'
import { PlaceLink } from './panel_navigation'
import type { PlaceStep } from '../../types/atlas'

interface DetailPanelProps {
  title: string
  /** What the title bar draws in place of the title, when there is no name to
      draw yet. The panel is still NAMED by `title`, which is what a screen
      reader is told. */
  heading?: ReactNode
  /** What kind of thing this is, set small above the title. Each panel passes
      its own `*-kind` line through here so the test id stays with the panel. */
  eyebrow?: ReactNode
  /** Changing this refocuses the panel, so a deep link reads from the top. */
  focusKey: string
  testId: string
  /**
   * How tall the sheet is to be HELD on a phone, as a CSS length, whatever it
   * has to say yet. Only a stand-in passes one (`pending_panel.tsx` has why):
   * a page's own panel is as tall as its content.
   */
  hold?: string
  children: ReactNode
}

// Whether the reader has pulled the phone's sheet up to its tall size, for the
// life of the tab. Outside any component, because a panel is a new component
// on every page — the stand-in, then the page's own — and a sheet that fell
// back to its short size at each step through a hierarchy would have to be
// pulled up again at each.
let sheetTall = false

/** For a test: as if the tab had just opened. */
export function forgetSheet() {
  sheetTall = false
}

/**
 * Whether the panel is ARRIVING or only changing what it describes.
 *
 * The page child remounts on every Inertia visit, so a panel cannot tell a
 * first open from a breadcrumb click on its own, and sliding the whole card in
 * again for the second would make every step through a hierarchy look like the
 * panel closed and reopened. The layout is the only thing that knows there was
 * a panel a moment ago, so it says so through this context; the default is
 * `true` because a panel rendered with no layout around it — a component test,
 * or a shell that has not learned to provide the value yet — is one that just
 * opened.
 */
export const PanelEnteringContext = createContext(true)

/**
 * When the panel's frame last appeared from nothing, as a `performance.now()`
 * reading — or null when nobody is keeping track.
 *
 * A click shows the frame at once and fills it when the page lands, and those
 * are two different components: the shell's stand-in, then the page's own
 * panel. The first one slides in; the second replaces it a few milliseconds or
 * a few hundred later, and is by then "only changing what it describes". Drawn
 * with no slide it would cut the entrance off wherever it had got to — on a
 * page that was already in memory, before it had visibly begun — and drawn
 * with a fresh one the card would jump back and slide in twice. So the
 * replacement is told when the entrance began and picks the animation up at
 * that point.
 */
export const PanelEnteredAtContext = createContext<number | null>(null)

/**
 * How a panel closes when there is a shell around it.
 *
 * On its own a panel closes by navigating home. Inside the shell that
 * navigation still happens, but the shell takes the panel off the screen
 * first — a round trip to learn that nothing is selected is not something a
 * reader should watch — and it is the shell that knows whether what is on
 * screen is a page's panel or a stand-in for one still on its way. Null with
 * no shell, which is every component test of a panel.
 */
export const PanelCloseContext = createContext<(() => void) | null>(null)

/**
 * Whether the reader has a winery FILTER on, so the map beside the panel is
 * drawing only some of the wineries it would.
 *
 * A panel counts and lists what the atlas holds for its place — "73 on the
 * map" — and the shell's filters are not its to know about. But with a filter
 * on, "on the map" was untrue: the list said 73 beside a map showing 25. The
 * shell says so through this, and the one heading that makes the claim stops
 * making it. False with no shell.
 */
export const MapFilteredContext = createContext(false)

// The longer of the two entrances (`--animate-panel-in-y`, in application.css).
// An entrance older than this is over and there is nothing to pick up.
const SLIDE_MS = 240

/**
 * The shell every detail panel shares: the close path, the focus contract and
 * the responsive frame. The place and grape panels each carried their own copy
 * before this existed, which meant the keyboard contract could drift between
 * two surfaces that must behave the same.
 *
 * The panel is URL-driven — it closes by navigating rather than by mutating
 * local state, which is what makes a click and a deep link produce identical
 * state. Closing moves focus out FIRST, because the panel is about to unmount
 * and the body is a dead end on a page whose only other content is a canvas.
 *
 * Bottom sheet on a phone, a card floating over the map from `md` up: R9 asks
 * for a full-screen map, and a phone panel covering it would contradict that.
 * The card is inset from the edges rather than docked to them so the map reads
 * as continuing behind it, which is what a printed label laid on a plate does.
 *
 * **The surface is opaque, and that is a speed decision as much as a look.**
 * The old `bg-white/95` let map labels ghost through the text, and the usual
 * cure — a backdrop blur — is re-rasterised on every frame the WebGL canvas
 * underneath moves. A solid surface costs nothing while the map is panning.
 *
 * Only `opacity` and `transform` animate, behind `motion-safe:`, and only on the
 * way in: closing unmounts this component, and an exit animation would need
 * the old DOM kept alive after its page has gone.
 *
 * The frame slides in when the panel ARRIVES and stays put when it only
 * changes what it describes (`PanelEnteringContext`); the body fades either
 * way, and that fade is the whole of a panel-to-panel step.
 */
export default function DetailPanel({ title, heading, eyebrow, focusKey, testId, hold, children }: DetailPanelProps) {
  const panel = useRef<HTMLElement>(null)
  const [tall, setTall] = useState(sheetTall)
  const resize = () => {
    sheetTall = !tall
    setTall(!tall)
  }
  const entering = useContext(PanelEnteringContext)
  const enteredAt = useContext(PanelEnteredAtContext)
  const closeInShell = useContext(PanelCloseContext)

  // How far into the entrance this panel starts, in milliseconds, or null for
  // no entrance at all. Decided ONCE, when the panel mounts: the shell
  // re-renders within the first frames of every open (the camera settles, a
  // layer arrives), by which time it no longer counts this as an arrival, and
  // a class taken away mid-animation is an animation cut short.
  const [slideFrom] = useState(() => {
    if (entering) return 0
    if (enteredAt === null) return null

    const elapsed = performance.now() - enteredAt

    return elapsed >= 0 && elapsed < SLIDE_MS ? elapsed : null
  })

  // One function for both the close control and Escape, so the two cannot drift
  // into closing the panel by different means.
  const close = useCallback(
    (event?: { preventDefault: () => void }) => {
      event?.preventDefault()
      document.getElementById(MAP_CONTAINER_ID)?.focus()

      if (closeInShell) closeInShell()
      else router.visit(MAP_ROOT_PATH)
    },
    [closeInShell],
  )

  useEffect(() => {
    panel.current?.focus()
  }, [focusKey])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [close])

  return (
    <aside
      ref={panel}
      tabIndex={-1}
      role="region"
      aria-label={`${title} details`}
      data-testid={testId}
      data-tall={tall ? '' : undefined}
      // This is the box that scrolls (`FRAME`), on a phone and on a desktop
      // alike. Marked so a part of the body can find it with `closest`: the
      // page behind a panel does not scroll, so `window.scrollBy` moves nothing.
      data-panel-scroll=""
      className={`${FRAME} ${slideFrom !== null ? SLIDE_IN : ''}`}
      style={{
        // A negative delay starts a CSS animation part-way through, which is
        // how this panel carries on the slide its stand-in began.
        ...(slideFrom ? { animationDelay: `-${Math.round(slideFrom)}ms` } : {}),
        // The two lengths the frame's phone height is built from (`FRAME`).
        ...(hold ? { '--sheet-hold': hold } : {}),
        ...(tall ? { '--sheet-max': SHEET_TALL } : {}),
      }}
    >
      {/* A `div`, not a `header`: the shell's own `<header>` is what the chrome
          tests select on, and a second one inside the panel would answer to the
          same selector. Sticky, so the name of what is being described and the
          way out both stay on screen however far the body is scrolled; opaque,
          so the body does not show through it on the way past. */}
      <div className="sticky top-0 z-10 flex items-start gap-1 border-b border-black/5 bg-surface-raised px-5 pb-3 pt-4">
        <div className="min-w-0 flex-1 pr-2">
          {eyebrow}
          <h1 className="mt-0.5 text-balance font-display text-[27px] font-semibold leading-[1.12] tracking-tight">
            {heading ?? title}
          </h1>
        </div>
        {/* ON A PHONE, HOW MUCH OF THE SCREEN THE SHEET TAKES. It is "up to"
            60dvh, which for a place with seventy wineries showed two of them,
            with no way to see more of the list at once and no way to get the
            map back short of closing. Now it has two sizes and one control
            that says which way it goes: up to nearly the whole screen, and
            back. A button and not a drag handle — a handle that does not drag
            is a drawing of a control — and remembered for the tab
            (`sheetTall`), so it is not asked again at every step. From `md`
            the panel is a card beside the map and has one size. */}
        <button
          type="button"
          data-testid="panel-resize"
          aria-pressed={tall}
          aria-label={tall ? 'Show more of the map' : 'Show more of this panel'}
          onClick={resize}
          className="-mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-soft hover:bg-black/5 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:hidden"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className={`h-4 w-4 ${tall ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m5 12 5-5 5 5" />
          </svg>
        </button>
        {/* A real href, so the control works with a keyboard, a middle click and
            a screen reader — but the click itself goes through `close`, which is
            the same path Escape takes. It is drawn as an icon because the word
            was the only underlined text in the corner of every panel; the name
            is unchanged, and the word itself stays in the markup for anything
            that reads text rather than ARIA. */}
        <Link
          href={MAP_ROOT_PATH}
          onClick={close}
          aria-label="Close panel"
          // 44px under a thumb, 40px under a pointer.
          className="-mr-2 -mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink-soft hover:bg-black/5 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:h-10 md:w-10"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="m5 5 10 10M15 5 5 15" />
          </svg>
          <span className="sr-only">Close</span>
        </Link>
      </div>

      {/* The body always fades, including when the shell around it stayed put:
          that is the whole of the panel-to-panel transition. */}
      <div className="px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-3 motion-safe:animate-content-in md:pb-10">
        {children}
      </div>

      {/* The paper fading in over the last of what is showing, so a list that
          runs on is seen to run on: without it the bottom row of a long panel
          was cut off hard by the panel's own edge, as if that were its end.
          It rides the bottom of the scrolling box and takes no clicks, and the
          body's last 40px are padding, so at the end of the scroll it lies
          over nothing. */}
      <div
        aria-hidden="true"
        className="pointer-events-none sticky bottom-0 -mt-12 h-12 bg-gradient-to-t from-surface-raised to-surface-raised/0"
      />
    </aside>
  )
}

// Below `md` the panel is an ordinary block: the shell's dock is a column
// anchored to the bottom of the screen, and the attribution and notices stack on
// top of the sheet only because the sheet is in that column's flow. From `md`
// the dock dissolves (`display: contents`) and the card positions itself against
// the shell.
//
// So this frame is only right INSIDE that dock (layouts/atlas_layout.tsx): the
// `<aside>` has to be the dock's direct child and its last one. It carried four
// bridge classes for a shell that mounted it straight into its root, where a
// block-level sheet would have landed at the top of the screen; no such shell
// is left, and a panel rendered outside a dock now is simply a block where it
// was put.
//
// `scroll-pt-24` is for the keyboard: the title bar is sticky, and without a
// scroll padding about its height a link tabbed to while scrolling back up
// comes to rest underneath it, focused and out of sight. `text-ink` is set here
// rather than inherited, so the card is the journal's ink on the journal's
// paper whatever colour the shell around it happens to set.
//
// ON A PHONE THE HEIGHT IS TWO LENGTHS. The most it may be, `--sheet-max`:
// 60dvh, or `SHEET_TALL` once the reader has asked for more of the panel. And
// the least it may be, `--sheet-hold`, which only a stand-in sets (the height
// of the sheet it replaced, or the cap) so that the sheet does not drop and
// climb back while a page is on its way. Custom properties rather than
// classes, because both are values a class cannot know.
//
// The corners are 1.75rem, a size up from the chrome's popovers: against the
// fully rounded pills round it a 1rem corner made the panel the squarest thing
// on the page.
//
// `scrollbar-gutter: stable`, for the browsers whose scrollbars take room
// (Chrome on Linux and Windows: fifteen pixels; a Mac or a phone draws them
// over the content and this does nothing there). A panel that fits has no
// scrollbar and one that does not has one, and a panel crosses that line while
// it is being read: opening a fact's quote can be the row that makes it
// scroll. Without the room kept, every line in the card moved fifteen pixels
// left under the pointer at that moment, the control just pressed included.
const FRAME =
  'pointer-events-auto relative max-h-[var(--sheet-max,60dvh)] scroll-pt-24 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] rounded-t-[1.75rem] bg-surface-raised text-ink shadow-panel ring-1 ring-black/5 outline-none max-md:min-h-[min(var(--sheet-hold,0px),var(--sheet-max,60dvh))] md:absolute md:bottom-3 md:right-3 md:top-3 md:z-30 md:max-h-none md:w-96 md:rounded-[1.75rem]'

// The sheet at its tall size: the screen, less the header's two rows on a
// phone (`CHROME_TOP_PHONE_PX`, 104px) and the credit, which rides on top of
// the sheet in the same column and must not be pushed up under the header.
const SHEET_TALL = 'calc(100dvh - 10rem)'

// Up 24px on a phone, in 16px from the right on a desktop — the direction each
// one came from.
const SLIDE_IN = 'motion-safe:animate-panel-in-y md:motion-safe:animate-panel-in-x'

/*
 * What the three panel bodies share. They live beside the shell rather than in
 * each panel for the reason the shell exists at all: a place, a grape and a
 * winery are read in the same card, one after another, and a breadcrumb or a
 * section heading that differs between them reads as three products.
 *
 * The scale, smallest to largest, so two neighbouring levels never look alike:
 * a caption (`text-xs`, soft), a group label (small caps, soft), a row of the
 * record (a soft label, a medium value), a section heading (Fraunces), a stat
 * tile's value (Fraunces, a step under the title) and the title (Fraunces, in
 * the shell). The old panels set both the section and the group in tiny grey
 * capitals, so "PROFILE" and "VISITING" read as the same level.
 *
 * And the ORDER every body keeps, which is the order a reader asks in: where
 * it sits (the trail), what to decide on (the stat tiles), the record (a
 * table), and then the sections — the evidence, the lists — each under a
 * heading of its own.
 */

/** A link inside a panel: the accent, with an underline that firms up on hover. */
export const PANEL_LINK =
  'text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

/** A caveat or a provenance line: the quietest text on the panel. */
export const PANEL_NOTE = 'text-xs leading-relaxed text-ink-soft'

/** A sentence standing in for a section that has nothing to show. */
export const PANEL_EMPTY = 'text-[13px] leading-relaxed text-ink-soft'

/**
 * The line that opens the rest of a list kept behind "Show all": a whole-width
 * row, 44px under a thumb and 40px under a pointer, like the rows over it.
 */
export const PANEL_SHOW_REST =
  '-mx-2 flex min-h-11 cursor-pointer list-none items-center rounded-lg px-2 text-sm font-medium text-accent hover:bg-black/[0.03] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent md:min-h-10 [&::-webkit-details-marker]:hidden'

// The one recessed surface on the paper: the land colour of the map itself
// (`PLAIN_BACKGROUND_COLOR`), with a hairline round it. The stat tiles sit in
// it and so does every piece of evidence, so the panel has two surfaces — the
// paper, and a well cut into it — and not a third. It was `bg-white/70`, a
// LIGHTER card on the paper, which made each piece of evidence the brightest
// thing on a panel whose headline is supposed to be the name.
//
// `--color-well` in application.css, which is that constant's copy for the
// stylesheet; `detail_panel.test.tsx` holds the two equal.
const WELL_SURFACE = 'bg-well'
const WELL = `${WELL_SURFACE} ring-1 ring-inset ring-ink/[0.06]`

/** One piece of evidence: a claim with where it came from. */
export const PANEL_CARD = `rounded-xl px-3 py-2.5 text-sm ${WELL}`

/** A block of someone else's words, set apart from the atlas's own. */
export const PANEL_QUOTED = `rounded-xl p-3 ${WELL}`

/** The label over a group inside a section — one level below the heading. */
export const PANEL_GROUP_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft'

/** A trailing count beside a heading, in the body face so it does not read as part of the name. */
export const PANEL_COUNT = 'ml-1 font-sans text-xs font-normal tabular-nums text-ink-soft'

// Explicit locale, like every other number a panel prints: one grouped by the
// viewer's locale would disagree with the one the SSR bundle grouped.
const COUNT = new Intl.NumberFormat('en-US')

/*
 * THE RECORD IS A TABLE. A label column, a value column and, where a fact has
 * a sentence behind it, a third column for the way to that sentence — every
 * row ruled off from the one above with a hairline. The rows used to be a
 * definition list with no rules (a place) and a stack of label-over-value
 * blocks under small-capital group names (a producer), and a producer with
 * fourteen facts was two screens of it. As a table the same fourteen are a
 * column of labels and a column of answers, read down.
 *
 * These three are the row's parts, shared by the place's rows (`PanelFact`)
 * and the producer's (`winery_profile.tsx`) so the two cannot become two
 * tables. Each user adds its own `grid-cols-*`: the label column is one width
 * per table, never per row, which is what lines the values up.
 */
export const PANEL_ROW = 'grid items-baseline gap-x-3 border-t border-ink/[0.09] py-2'
export const PANEL_ROW_LABEL = 'text-[12.5px] text-ink-soft'
export const PANEL_ROW_VALUE = 'min-w-0 font-medium'

interface PanelEyebrowProps {
  testId: string
  /**
   * The mark this thing has on the map, drawn in front of its kind: the amber
   * ribbon of the selected place, the ringed dot of the selected producer. It
   * is what ties the card to the one amber thing on the plate. A swatch, so it
   * is `aria-hidden` and adds nothing to the line's text.
   */
  mark?: ReactNode
  children: ReactNode
}

/**
 * The kind line the shell sets above the title. The accent rather than grey:
 * it is the one word that says which of three different things the card holds.
 */
export function PanelEyebrow({ testId, mark, children }: PanelEyebrowProps) {
  return (
    <p
      data-testid={testId}
      className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent"
    >
      {mark}
      {children}
    </p>
  )
}

// A crumb, and the crumb for the place the map has drawn in the selection
// colour: `--color-selected`, which is `SELECTED_LINE_COLOR` in map_config.ts
// — the line of the amber ribbon — as the stylesheet holds it.
// `detail_panel.test.tsx` compares the two.
//
// On a phone a crumb is a 44px target: 12px of padding above and below a 19px
// line, taken back by a negative margin so the trail is no taller for it.
const CRUMB =
  'py-1 underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent max-md:-my-2 max-md:inline-block max-md:py-3'
const CRUMB_PLAIN = `${CRUMB} decoration-ink-soft/30 hover:text-accent hover:decoration-accent`
const CRUMB_MARKED = `${CRUMB} font-medium text-selected decoration-selected/40 hover:decoration-selected`

interface PanelBreadcrumbProps {
  steps: PlaceStep[]
  current?: string
  /**
   * The step the map is showing in the selection colour, which takes that
   * colour here: a producer's appellation, the one amber outline on the plate.
   * `note` hangs a footnote mark on it, for a caveat that used to sit under
   * the trail as three lines of small print and now waits at the foot of the
   * record. The mark is a drawing; the note itself names what it is about.
   */
  marked?: { slug: string; note?: string }
}

/**
 * Where this sits, outermost first.
 *
 * `current` is the slug of the thing the panel describes, when the trail
 * includes it. It stays in the list for a screen reader — that is what
 * `aria-current` is for — and is hidden from sight, because the title directly
 * above already says it: a country's trail is only itself, and the old panel
 * printed "France" twice in a row. When nothing else is left the whole trail is
 * hidden the same way, so an empty list leaves no gap.
 */
export function PanelBreadcrumb({ steps, current, marked }: PanelBreadcrumbProps) {
  const ancestors = steps.filter((step) => step.slug !== current)

  return (
    <nav aria-label="Hierarchy" className={ancestors.length > 0 ? undefined : 'sr-only'}>
      {/* A SEPARATOR IS DRAWN ONLY BETWEEN TWO CRUMBS ON ONE LINE. Each crumb
          but the first hangs its "›" in the gap to its left, and the list
          clips what falls outside it — so the first crumb of a wrapped line
          has its mark past the edge, unseen, and no line starts or ends with
          one. (The mark used to trail its crumb, on the reasoning that a line
          ending in "›" says there is more; in a 24rem card it mostly said that
          something had been cut off. `BasemapAttribution` hangs its dots the
          same way.) The list's own padding is room for a crumb's focus ring,
          which the clip would otherwise shave. */}
      <ol className="-mx-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 overflow-hidden px-1 py-0.5 text-[13px] text-ink-soft">
        {steps.map((step) =>
          step.slug === current ? (
            <li key={step.slug} className="sr-only">
              <span aria-current="page">{step.name}</span>
            </li>
          ) : (
            <li
              key={step.slug}
              className="relative flex items-center gap-1.5 before:absolute before:right-full before:w-4 before:text-center before:content-['›']"
            >
              {/* A panel is already open here by definition, so every link out
                  of it replaces: on a map, Back means "close this". */}
              <PlaceLink place={step} className={step.slug === marked?.slug ? CRUMB_MARKED : CRUMB_PLAIN}>
                {step.name}
              </PlaceLink>
              {step.slug === marked?.slug && marked.note && (
                <sup aria-hidden="true" className="-ml-1 text-[10px] text-ink-soft">
                  {marked.note}
                </sup>
              )}
            </li>
          ),
        )}
      </ol>
    </nav>
  )
}

/**
 * THE FACTS A READER DECIDES ON, BEFORE ANYTHING ELSE: two or three figures in
 * the display face, each over a word saying what it is.
 *
 * A panel used to open with its longest text — a caveat, a breadcrumb, a
 * description — and the one thing most readers came for (can I walk in? how
 * many wineries are here?) was a row somewhere below. The tiles are that row
 * lifted to the top and set large enough to be read without reading.
 *
 * A TILE EXISTS ONLY FOR A FACT THE RECORD HOLDS. There is no placeholder
 * tile and no dash: an absent fact is an absent tile, the row is as many tiles
 * as there are, and with none there is no row. "Walk-in: —" would be the map
 * claiming to have asked.
 *
 * They wrap, and the row is built so that a wrap looks meant. Each tile is as
 * wide as its value and grows to share the row; when three do not fit — a
 * 320px phone, or "Appointment" beside "Independent" — the last takes a row of
 * its own at full width. The hairlines between them are the container's own
 * colour showing through a 1px gap, so they come out right however the tiles
 * fall, which a border on each tile would not.
 *
 * A definition list, with each pair written term-first and DRAWN value-first:
 * a screen reader hears "visiting, Walk-in", the eye sees "Walk-in" over
 * "visiting".
 */
export function PanelTiles({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <dl data-testid={testId} className="mt-3 flex flex-wrap gap-px overflow-hidden rounded-2xl bg-ink/10 p-px">
      {children}
    </dl>
  )
}

export function PanelTile({ label, testId, children }: { label: string; testId?: string; children: ReactNode }) {
  return (
    <div
      data-testid={testId}
      className={`flex min-w-fit flex-1 flex-col-reverse justify-end px-2.5 pb-2 pt-2.5 ${WELL_SURFACE}`}
    >
      <dt className="mt-1 whitespace-nowrap text-[11px] leading-tight text-ink-soft">{label}</dt>{' '}
      <dd className="whitespace-nowrap font-display text-lg font-semibold leading-[1.1] tracking-[-0.012em] tabular-nums lining-nums">
        {children}
      </dd>
    </div>
  )
}

/**
 * Label-and-value rows, as a definition list — the journal's own pattern
 * (`pages/tastings/show.tsx`) — set as a ruled table (see `PANEL_ROW`).
 */
interface PanelFactsProps {
  /** A wider label column, for a list whose labels run to two words. One width
      per list, never per row: the point of the column is that the values line up. */
  wide?: boolean
  /**
   * No rule above the first row. For a table that opens directly under
   * something with an edge of its own — the title bar, the stat tiles — where
   * a hairline a few pixels below that edge reads as a drafting error. Under a
   * section heading the first rule stays: it is what closes the heading.
   */
  flush?: boolean
  className?: string
  children: ReactNode
}

export function PanelFacts({ wide = false, flush = false, className = 'mt-3', children }: PanelFactsProps) {
  return (
    <dl
      data-wide={wide ? '' : undefined}
      data-flush={flush ? '' : undefined}
      className={`group/facts text-[13.5px] leading-snug ${className}`}
    >
      {children}
    </dl>
  )
}

/**
 * One row. The test id sits on the row, so a text assertion still sees the
 * label and the value together — and the row is a real box rather than
 * `display: contents`, which would leave the test id on an element with no
 * geometry for a browser test to measure.
 *
 * The space between the term and the definition is not layout — a grid ignores
 * it — it is what makes the row read "States CA" rather than "StatesCA" to
 * anything that takes the text and not the boxes.
 */
export function PanelFact({ label, testId, children }: { label: string; testId?: string; children: ReactNode }) {
  return (
    <div
      data-testid={testId}
      className={`${PANEL_ROW} grid-cols-[5.25rem_minmax(0,1fr)] group-data-[wide]/facts:grid-cols-[7rem_minmax(0,1fr)] group-data-[flush]/facts:first:border-t-0 group-data-[flush]/facts:first:pt-0`}
    >
      <dt className={PANEL_ROW_LABEL}>{label}</dt> <dd className={PANEL_ROW_VALUE}>{children}</dd>
    </div>
  )
}

interface PanelSectionProps {
  title: string
  /** How many things the section lists, when that is worth knowing before reading them. */
  count?: number
  /**
   * What that number is a count OF, where the heading alone would overclaim:
   * "Wineries 73" reads as how many there are, "Wineries 73 on the map" as how
   * many the atlas draws. It follows the number, in the number's own voice.
   */
  counted?: string
  /**
   * A line set small at the far end of the heading: where the section's
   * contents come from, said once for the section instead of once per row.
   */
  note?: ReactNode
  testId?: string
  children: ReactNode
}

/**
 * A titled part of the body, ruled off from whatever came before it.
 *
 * "Whatever came before it" is meant literally. The rule and the space above it
 * apply only when an earlier sibling is actually drawn, which CSS can say and
 * React cannot cheaply: a country's panel opens with a breadcrumb that is
 * present but `sr-only`, and its first section would otherwise sit under a
 * second hairline an empty band below the title bar's own.
 *
 * SECTIONS READ AS A LEVEL OF THEIR OWN. Inside a section everything is now
 * ruled — table rows, list rows — so a section cannot be told from a row by a
 * hairline any more. It is told by its heading, a size up in the display face,
 * and by the space above it, which is more than any row has.
 */
export function PanelSection({ title, count, counted, note, testId, children }: PanelSectionProps) {
  return (
    <section
      data-testid={testId}
      className="border-ink/[0.09] pt-1 [:not(.sr-only)~&]:mt-7 [:not(.sr-only)~&]:border-t [:not(.sr-only)~&]:pt-5"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[19px] font-semibold leading-tight tracking-[-0.01em]">
          {title}
          {/* The space is for anything reading the text rather than the layout:
              without it the heading's name is "Within it4". */}
          {count !== undefined && ' '}
          {count !== undefined && (
            <span className={PANEL_COUNT}>
              {COUNT.format(count)}
              {counted && ` ${counted}`}
            </span>
          )}
        </h2>
        {note}
      </div>
      {children}
    </section>
  )
}
